import { useState, useEffect, useCallback, useMemo, useRef, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import * as echarts from 'echarts';
import {
  siteDataApi,
  type PortalPeriod,
  type PortalCoreCard,
  type PortalTrendRow,
  type PortalSessionRow,
  type PortalUserRow,
  type PortalPayUserRow,
  type PortalProductRow,
  type PortalParams,
  type VooglamPortalSessionMultidimRow,
  type VooglamSessionComparisonRow,
} from '../api/siteData';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { ReportExportButton } from '../components/ReportExportButton';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { ChannelPortalSavedViews } from '../components/ChannelPortalSavedViews';
import { capturePortalViewSection, isPortalViewSnapshot, type PortalViewSnapshot } from '../lib/channelPortalViews';
import { MultiSelect, type SelectOption } from '../components/MultiSelect';
import { getCurrentCalendarRange, MarketingDateRangePicker, type MarketingTimeGrain } from '../components/MarketingTimeRangeFilter';
import Pagination from '../components/Pagination';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import '../components/DataUpdateNote.css';
import { SortableHeader, useTableSort, type SortValue } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { useSiteShopName } from '../hooks/useSiteShopName';
import { useLazySection } from '../hooks/useLazySection';
import { useAppStore } from '../store/useAppStore';
import { formatSiteMetric, isSiteSensitiveMetric, maskSiteMetric } from '../lib/siteDataSecurity';

const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtNum = (v: number) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtPct = (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%');

function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function rangeFor(period: PortalPeriod): { start: string; end: string } {
  const t = new Date(); const y = t.getFullYear(); const m = t.getMonth();
  if (period === 'day') {
    const end = new Date(t); end.setDate(end.getDate() - 1);
    const start = new Date(end); start.setDate(start.getDate() - 6);
    return { start: fmtLocalDate(start), end: fmtLocalDate(end) };
  }
  if (period === 'week') {
    const end = new Date(t); end.setDate(end.getDate() - 1);
    const start = new Date(end); start.setDate(start.getDate() - 27);
    return { start: fmtLocalDate(start), end: fmtLocalDate(end) };
  }
  if (period === 'month') return { start: fmtLocalDate(new Date(y, m, 1)), end: fmtLocalDate(new Date(y, m + 1, 0)) };
  return { start: fmtLocalDate(new Date(y, 0, 1)), end: fmtLocalDate(new Date(y, 11, 31)) };
}

function lastCompletedWeekRange(): { start: string; end: string } {
  const today = new Date();
  const dow = today.getDay() || 7;
  const thisMonday = new Date(today);
  thisMonday.setDate(today.getDate() - dow + 1);
  const end = new Date(thisMonday);
  end.setDate(thisMonday.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return { start: fmtLocalDate(start), end: fmtLocalDate(end) };
}

function quickRanges(): { key: string; label: string; start: string; end: string }[] {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const recent = (days: number) => {
    const start = new Date(today);
    start.setDate(today.getDate() - days + 1);
    return { start: fmtLocalDate(start), end: fmtLocalDate(today) };
  };
  const week = lastCompletedWeekRange();
  return [
    { key: 'yesterday', label: '昨天', start: fmtLocalDate(yesterday), end: fmtLocalDate(yesterday) },
    { key: 'recent7', label: '最近7天', ...recent(7) },
    { key: 'recent14', label: '最近14天', ...recent(14) },
    { key: 'recent30', label: '最近30天', ...recent(30) },
    { key: 'thisWeek', label: '本周', ...getCurrentCalendarRange('thisWeek', fmtLocalDate(today)) },
    { key: 'thisMonth', label: '本月', ...getCurrentCalendarRange('thisMonth', fmtLocalDate(today)) },
    { key: 'lastWeek', label: '上个自然周', start: week.start, end: week.end },
    { key: 'custom', label: '自定义', start: '', end: '' },
  ];
}

// 核心指标基于日汇总数据，最近 N 天不包含尚未完成同步的今天。
function coreQuickRanges(): { key: string; label: string; start: string; end: string }[] {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const recent = (days: number) => {
    const start = new Date(yesterday);
    start.setDate(start.getDate() - days + 1);
    return { start: fmtLocalDate(start), end: fmtLocalDate(yesterday) };
  };
  const week = lastCompletedWeekRange();
  return [
    { key: 'yesterday', label: '昨天', start: fmtLocalDate(yesterday), end: fmtLocalDate(yesterday) },
    { key: 'recent7', label: '最近7天', ...recent(7) },
    { key: 'recent14', label: '最近14天', ...recent(14) },
    { key: 'recent30', label: '最近30天', ...recent(30) },
    { key: 'thisWeek', label: '本周', ...getCurrentCalendarRange('thisWeek', fmtLocalDate(today)) },
    { key: 'thisMonth', label: '本月', ...getCurrentCalendarRange('thisMonth', fmtLocalDate(today)) },
    { key: 'lastWeek', label: '上个自然周', start: week.start, end: week.end },
    { key: 'custom', label: '自定义', start: '', end: '' },
  ];
}

const ALL_PERIODS: { key: PortalPeriod; label: string }[] = [
  { key: 'day', label: '日' }, { key: 'week', label: '周' }, { key: 'month', label: '月' }, { key: 'year', label: '年' },
];
// BI 中一级渠道未选择时表示统计全部渠道，前端与其保持同一口径。
const DEFAULT_CHANNELS = '';
// 会话报表与观远口径保持一致，默认仅统计用户营销和付费广告。
const DEFAULT_SESSION_CHANNELS = '用户营销,付费广告';
const CORE_INTEGER_METRIC_KEYS = new Set(['session', 'order', 'first_user']);
const ZEELOOL_REGIONAL_PORTAL_REGIONS = new Set(['ca', 'uk', 'de', 'fr']);
const ZEELOOL_REGIONAL_HIDDEN_METRICS = new Set(['spend', 'session_cost', 'cvr', 'cpa', 'roi']);
function channelPortalMetricLabel(label: string): string {
  if (label === 'CPA') return '渠道CPA';
  if (label === 'ROI') return '渠道ROI';
  return label;
}

type PortalTrendMetricKey = Exclude<keyof PortalTrendRow, 'date'>;
type PortalTrendMetric = {
  key: PortalTrendMetricKey;
  label: string;
  format: 'number' | 'integer' | 'percent';
};

const PORTAL_TREND_VALUE_METRICS: readonly PortalTrendMetric[] = [
  { key: 'session_num', label: '神策会话数', format: 'integer' },
  { key: 'net_sales', label: '净销售额', format: 'number' },
  { key: 'order_num', label: '支付订单数', format: 'integer' },
  { key: 'first_user_num', label: '首单用户数', format: 'integer' },
  { key: 'spend', label: '花费', format: 'number' },
];
const PORTAL_TREND_RATIO_METRICS: readonly PortalTrendMetric[] = [
  { key: 'reg_first_rate', label: '注册当日首单率', format: 'percent' },
  { key: 'session_cost', label: '会话成本', format: 'number' },
  { key: 'cvr', label: 'CVR', format: 'percent' },
  { key: 'cpa', label: '渠道CPA', format: 'number' },
  { key: 'roi', label: '渠道ROI', format: 'number' },
];
const DEFAULT_TREND_VALUE_KEYS: PortalTrendMetricKey[] = ['session_num', 'net_sales', 'order_num', 'first_user_num', 'spend'];
const DEFAULT_TREND_RATIO_KEYS: PortalTrendMetricKey[] = ['reg_first_rate', 'session_cost', 'roi'];
const portalTrendMetricByKey = new Map<PortalTrendMetricKey, PortalTrendMetric>(
  [...PORTAL_TREND_VALUE_METRICS, ...PORTAL_TREND_RATIO_METRICS].map(metric => [metric.key, metric]),
);

type PortalUserDimensionKey = 'date' | 'one_level_channel' | 'two_level_channel' | 'three_level_channel' | 'country' | 'device';
const PORTAL_USER_DIMENSIONS: readonly { key: PortalUserDimensionKey; label: string }[] = [
  { key: 'date', label: '日期' },
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'three_level_channel', label: '三级渠道' },
  { key: 'country', label: '国家' },
  { key: 'device', label: '终端' },
];
const DEFAULT_USER_DIMENSIONS: PortalUserDimensionKey[] = ['date', 'three_level_channel'];
const portalUserDimensionOptions: readonly SelectOption[] = PORTAL_USER_DIMENSIONS.map(dimension => ({ value: dimension.key, label: dimension.label }));

type PortalSessionDimensionKey = 'date' | 'one_level_channel' | 'two_level_channel' | 'three_level_channel' | 'country' | 'source' | 'medium' | 'campaign' | 'device';
const PORTAL_SESSION_DIMENSIONS: readonly { key: PortalSessionDimensionKey; label: string }[] = [
  { key: 'date', label: '日期' },
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'three_level_channel', label: '三级渠道' },
  { key: 'country', label: '国家' },
  { key: 'source', label: '来源' },
  { key: 'medium', label: '媒介' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'device', label: '终端' },
];
const DEFAULT_SESSION_DIMENSIONS: PortalSessionDimensionKey[] = ['date', 'three_level_channel'];
const portalSessionDimensionOptions: readonly SelectOption[] = PORTAL_SESSION_DIMENSIONS.map(dimension => ({ value: dimension.key, label: dimension.label }));

type VooglamSessionDimensionKey = 'date' | 'two_level_channel' | 'three_level_channel';
const VOOGLAM_SESSION_DIMENSIONS: readonly { key: VooglamSessionDimensionKey; label: string }[] = [
  { key: 'date', label: '日期' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'three_level_channel', label: '三级渠道' },
];
const DEFAULT_VOOGLAM_SESSION_DIMENSIONS: VooglamSessionDimensionKey[] = ['date', 'three_level_channel'];
const vooglamSessionDimensionOptions: readonly SelectOption[] = VOOGLAM_SESSION_DIMENSIONS.map(dimension => ({ value: dimension.key, label: dimension.label }));

type PortalPayUserDimensionKey = 'period' | 'one_level_channel' | 'two_level_channel' | 'three_level_channel' | 'country' | 'device';
const PORTAL_PAY_USER_DIMENSIONS: readonly { key: PortalPayUserDimensionKey; label: string }[] = [
  { key: 'period', label: '周' },
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'three_level_channel', label: '三级渠道' },
  { key: 'country', label: '国家' },
  { key: 'device', label: '终端' },
];
const DEFAULT_PAY_USER_DIMENSIONS: PortalPayUserDimensionKey[] = ['period', 'three_level_channel'];
const portalPayUserDimensionOptions = (period: Extract<PortalPeriod, 'week' | 'month'>): readonly SelectOption[] => PORTAL_PAY_USER_DIMENSIONS.map(dimension => ({
  value: dimension.key,
  label: dimension.key === 'period' ? (period === 'month' ? '月' : '周') : dimension.label,
}));

type PortalProductDimensionKey = 'date' | 'one_level_channel' | 'two_level_channel' | 'three_level_channel' | 'product_sku' | 'business_sku' | 'shop_sku';
const PORTAL_PRODUCT_DIMENSIONS: readonly { key: PortalProductDimensionKey; label: string }[] = [
  { key: 'date', label: '日期' },
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'three_level_channel', label: '三级渠道' },
  { key: 'product_sku', label: '商品SKU' },
  { key: 'business_sku', label: '平台SKU' },
  { key: 'shop_sku', label: '销售SKU' },
];
const DEFAULT_PRODUCT_DIMENSIONS: PortalProductDimensionKey[] = ['date', 'three_level_channel', 'product_sku', 'business_sku', 'shop_sku'];
const portalProductDimensionOptions: readonly SelectOption[] = PORTAL_PRODUCT_DIMENSIONS.map(dimension => ({ value: dimension.key, label: dimension.label }));

function fixedPortalDimensionOrder<T extends string>(
  values: readonly string[],
  dimensions: readonly { key: T; label: string }[],
  defaults: readonly T[],
): T[] {
  const selected = new Set(values);
  const ordered = dimensions.filter(dimension => selected.has(dimension.key)).map(dimension => dimension.key);
  return ordered.length > 0 ? ordered : [...defaults];
}

const tdNum: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const thBase: React.CSSProperties = { background: 'var(--surface2)', whiteSpace: 'nowrap' };

function Card({ title, children, extra, allowOverflow = false }: { title: string; children: ReactNode; extra?: ReactNode; allowOverflow?: boolean }) {
  return (
    <div className="card" style={{ padding: 0, overflow: allowOverflow ? 'visible' : 'hidden', marginBottom: 16, position: 'relative', zIndex: allowOverflow ? 1 : undefined }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
        {extra}
      </div>
      {children}
    </div>
  );
}

function TextFilter({
  label,
  value,
  onChange,
  placeholder = '全部',
  size = '',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'lg' | 'xl' | '';
}) {
  const sizeClass = size ? ` ${size}` : '';
  return (
    <div className={`data-filter-field${sizeClass}`}>
      <label className="data-filter-label">{label}</label>
      <input className={`form-input data-filter-input${sizeClass}`} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

const splitCsvValue = (value: string) => value.split(',').map(v => v.trim()).filter(Boolean);
type PortalFilterKey = 'channels' | 'two_level' | 'three_level' | 'country' | 'device' | 'source' | 'medium' | 'full_name';
type PortalFilterExclusions = Partial<Record<PortalFilterKey, boolean>>;
const exclusionParam = (filters: PortalFilterExclusions) => Object.entries(filters).filter(([, enabled]) => enabled).map(([key]) => key).join(',');

const PORTAL_LABEL_FIELDS = ['store_name', 'one_level_channel', 'two_level_channel', 'three_level_channel', 'country', 'pub_zl_utm_source', 'pub_zl_utm_medium'] as const;

function PortalSelectFilter({
  label, value, onChange, field, exclude = false, onExcludeChange, size = '',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  field: typeof PORTAL_LABEL_FIELDS[number];
  exclude?: boolean;
  onExcludeChange?: (exclude: boolean) => void;
  size?: 'sm' | 'lg' | 'xl' | '';
}) {
  const sizeClass = size ? ` ${size}` : '';
  return (
    <div className={`data-filter-field${sizeClass}`}>
      <label className="data-filter-label">{label}</label>
      <ReportLabelSelect dataset="channel_portal" field={field} pageFields={PORTAL_LABEL_FIELDS} includeAll value={value && value !== '全部' ? splitCsvValue(value) : ['全部']} onChange={vals => onChange(vals.includes('全部') ? '' : vals.join(','))} exclude={exclude} onExcludeChange={onExcludeChange} allowExcludeAll paginated={field === 'pub_zl_utm_source' || field === 'pub_zl_utm_medium'} width="100%" />
    </div>
  );
}

function PortalMultiSelectFilter({
  label, value, onChange, field, exclude = false, onExcludeChange, size = '',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  field: typeof PORTAL_LABEL_FIELDS[number];
  exclude?: boolean;
  onExcludeChange?: (exclude: boolean) => void;
  size?: 'sm' | 'lg' | 'xl' | '';
}) {
  const sizeClass = size ? ` ${size}` : '';
  return (
    <div className={`data-filter-field${sizeClass}`}>
      <label className="data-filter-label">{label}</label>
      <ReportLabelSelect dataset="channel_portal" field={field} pageFields={PORTAL_LABEL_FIELDS} includeAll value={value ? splitCsvValue(value) : ['全部']} onChange={vals => onChange(vals.includes('全部') ? '' : vals.join(','))} exclude={exclude} onExcludeChange={onExcludeChange} allowExcludeAll paginated={field === 'pub_zl_utm_source' || field === 'pub_zl_utm_medium'} width="100%" />
    </div>
  );
}

function PortalProductCategoryFilter({
  site,
  value,
  onChange,
  exclude = false,
  onExcludeChange,
}: {
  site: string;
  value: string[];
  onChange: (value: string[]) => void;
  exclude?: boolean;
  onExcludeChange?: (exclude: boolean) => void;
}) {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loadedSite, setLoadedSite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setOptions([]);
    setLoadedSite('');
    setError('');
  }, [site]);

  const loadOptions = useCallback(async () => {
    if (!site || loading || loadedSite === site) return;
    setLoading(true);
    setError('');
    try {
      const response = await siteDataApi.getPortalOptions({ field: 'product_category', site });
      setOptions((response.data?.list ?? []).map(item => ({ value: item, label: item })));
      setLoadedSite(site);
    } catch (e) {
      setError(e instanceof Error ? `商品品类加载失败：${e.message}` : '商品品类加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [loadedSite, loading, site]);

  return (
    <div className="data-filter-field lg">
      <label className="data-filter-label">商品品类</label>
      <MultiSelect
        options={options}
        value={value}
        onChange={onChange}
        placeholder="全部"
        countLabelFormatter={count => `已选 ${count} 项`}
        searchable
        clearable
        tagDisplay
        onOpen={loadOptions}
        loading={loading}
        errorText={error}
        emptyText={loadedSite === site ? '暂无可选商品品类' : '打开后加载商品品类'}
        exclude={exclude}
        onExcludeChange={onExcludeChange}
        style={{ width: '100%' }}
      />
    </div>
  );
}

function DateRangeInputs({
  start,
  end,
  grain = 'day',
  onStart,
  onEnd,
}: {
  start: string;
  end: string;
  grain?: MarketingTimeGrain;
  onStart: (value: string) => void;
  onEnd: (value: string) => void;
}) {
  return <MarketingDateRangePicker grain={grain} start={start} end={end} max={fmtLocalDate(new Date())} onChange={({ start: nextStart, end: nextEnd }) => { onStart(nextStart); onEnd(nextEnd); }} />;
}

const portalCoreExportColumns = [{ key: 'label', label: '指标' }, { key: 'value', label: '值' }, { key: 'dod', label: '日环比' }, { key: 'wow', label: '周同比' }];
const portalTrendExportColumns = (keys: PortalTrendMetricKey[]) => [
  { key: 'date', label: '日期' },
  ...keys.map(key => ({ key, label: portalTrendMetricByKey.get(key)?.label ?? key })),
];
const portalSessionExportColumns = (dimensions: PortalSessionDimensionKey[]) => [
  ...dimensions.map(key => ({ key, label: PORTAL_SESSION_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key })),
  { key: 'session_num', label: '会话数(神策)' },
  { key: 'regist_user_num', label: '注册用户数' }, { key: 'pdp_view_num', label: '商品详情页浏览次数' }, { key: 'add_cart_num', label: '加购成功次数' },
  { key: 'order_num', label: '支付订单数' }, { key: 'net_sales', label: '净销售额' }, { key: 'avg_price', label: '单均价' },
  { key: 'session_reg_rate', label: '会话注册率' }, { key: 'cvr', label: 'CVR' }, { key: 'session_cart_rate', label: '会话加购率' }, { key: 'cart_conv_rate', label: '加购转化率' },
];
const portalUserExportColumns = (dimensions: PortalUserDimensionKey[]) => [
  ...dimensions.map(key => ({ key, label: PORTAL_USER_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key })),
  { key: 'visit_user_num', label: '访问UV' },
  { key: 'no_reg_visit_num', label: '未注册访问UV' }, { key: 'no_reg_visit_rate', label: '未注册访问用户占比' },
  { key: 'first_visit_num', label: '首次访问UV' }, { key: 'first_visit_rate', label: '首次访问UV占比' }, { key: 'reg_num', label: '注册用户数' }, { key: 'reg_rate', label: '当日注册率' },
];
const portalPayUserExportColumns = (dimensions: PortalPayUserDimensionKey[], period: 'week' | 'month') => [
  ...dimensions.map(key => ({ key, label: key === 'period' ? (period === 'month' ? '月' : '周') : PORTAL_PAY_USER_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key })),
  { key: 'first_order_reg_cur', label: '首单当期注册' },
  { key: 'first_order_reg_year', label: '首单当年非当期注册' }, { key: 'first_order_no_year', label: '首单非当年注册' },
  { key: 'new_repeat_num', label: '新客复购' }, { key: 'act_old_num', label: '活跃老客' }, { key: 'sleep_old_num', label: '休眠老客' },
];
const portalProductExportColumns = (dimensions: PortalProductDimensionKey[]) => [
  ...dimensions.map(key => ({ key, label: PORTAL_PRODUCT_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key })),
  { key: 'pdp_view_num', label: '商品详情页浏览次数' }, { key: 'add_cart_num', label: '加购成功次数' }, { key: 'quantity', label: '商品销量' },
  { key: 'sales_amt', label: '商品销售额' }, { key: 'avg_price', label: '商品均价' }, { key: 'view_conv_rate', label: '浏览销量转化率' },
];

type PortalSection = 'core' | 'trend' | 'session' | 'user' | 'payUser' | 'product' | 'vooglamSession' | 'vooglamComparison';

interface PortalExportSnapshot {
  params: PortalParams;
}

const emptyPortalLoading: Record<PortalSection, boolean> = {
  core: false,
  trend: false,
  session: false,
  user: false,
  payUser: false,
  product: false,
  vooglamSession: false,
  vooglamComparison: false,
};

const emptyPortalQueryVersions: Record<PortalSection, number> = {
  core: 0,
  trend: 0,
  session: 0,
  user: 0,
  payUser: 0,
  product: 0,
  vooglamSession: 0,
  vooglamComparison: 0,
};

function formatTrendMetric(value: number, format: PortalTrendMetric['format']): string {
  if (format === 'integer') return fmtInt(value);
  if (format === 'percent') return fmtPct(value);
  return fmtNum(value);
}

function TrendChart({
  rows,
  loading,
  valueMetrics,
  ratioMetrics,
}: {
  rows: PortalTrendRow[];
  loading: boolean;
  valueMetrics: PortalTrendMetricKey[];
  ratioMetrics: PortalTrendMetricKey[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const selectedMetrics = [...valueMetrics, ...ratioMetrics]
      .map(key => portalTrendMetricByKey.get(key))
      .filter((metric): metric is PortalTrendMetric => Boolean(metric));
    if (!ref.current || rows.length === 0 || selectedMetrics.length === 0) {
      chartRef.current?.dispose();
      chartRef.current = null;
      return;
    }
    if (!chartRef.current) chartRef.current = echarts.init(ref.current);
    const metricByLabel = new Map(selectedMetrics.map(metric => [metric.label, metric]));
    chartRef.current.setOption({
      color: ['#2f6fd6', '#8eb4ff', '#f4c516', '#5db6a5', '#a78bfa', '#f59e0b', '#ec6e8b', '#43a5e4', '#7087d9', '#94a3b8'],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: unknown) => {
          const points = Array.isArray(params) ? params as Array<{ axisValueLabel?: string; marker?: string; seriesName?: string; value?: number }> : [];
          if (points.length === 0) return '';
          return [
            points[0].axisValueLabel ?? '',
            ...points.map(point => {
              const metric = metricByLabel.get(point.seriesName ?? '');
              return `${point.marker ?? ''}${point.seriesName ?? ''}：${formatTrendMetric(Number(point.value ?? 0), metric?.format ?? 'number')}`;
            }),
          ].join('<br/>');
        },
      },
      legend: { bottom: 0, itemWidth: 10, itemHeight: 8 },
      grid: { left: 64, right: 58, top: 44, bottom: 58 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: rows.map(r => r.date),
        axisLine: { lineStyle: { color: '#d8dee9' } },
        axisLabel: { color: '#4b5563' },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: { color: '#4b5563' },
          splitLine: { lineStyle: { color: '#e8edf5' } },
        },
        {
          type: 'value',
          axisLabel: { color: '#4b5563' },
          splitLine: { show: false },
        },
      ],
      series: selectedMetrics.map(metric => ({
        name: metric.label,
        type: 'line' as const,
        smooth: true,
        yAxisIndex: valueMetrics.includes(metric.key) ? 0 : 1,
        symbol: valueMetrics.includes(metric.key) ? 'circle' : 'rect',
        symbolSize: 6,
        label: {
          show: true,
          position: 'top',
          distance: 6,
          fontSize: 10,
          formatter: (params: { value: number | string }) => formatTrendMetric(Number(params.value ?? 0), metric.format),
        },
        data: rows.map(row => Number(row[metric.key] ?? 0)),
      })),
    }, true);
  }, [rows, valueMetrics, ratioMetrics]);

  useEffect(() => {
    const resize = () => chartRef.current?.resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  useEffect(() => () => { chartRef.current?.dispose(); chartRef.current = null; }, []);

  return (
    <div style={{ position: 'relative' }}>
      {rows.length === 0 ? (
        loading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>暂无数据</div>
      ) : valueMetrics.length + ratioMetrics.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>请选择至少一个指标</div>
      ) : (
        <div ref={ref} style={{ width: '100%', height: 280 }} />
      )}
      {loading && rows.length > 0 && <TableLoadingOverlay />}
    </div>
  );
}

const VOOGLAM_SESSION_COMPARISON_METRICS: readonly { key: Exclude<keyof VooglamSessionComparisonRow, 'label' | 'is_growth'>; label: string; format: 'money' | 'integer' | 'rate' }[] = [
  { key: 'spend', label: '渠道花费', format: 'money' },
  { key: 'session_num', label: '会话', format: 'integer' },
  { key: 'session_cost', label: '会话成本', format: 'money' },
  { key: 'pdp_view_num', label: '商详页浏览次数', format: 'integer' },
  { key: 'add_cart_num', label: '加购次数', format: 'integer' },
  { key: 'order_num', label: '支付订单数', format: 'integer' },
  { key: 'net_sales', label: '净销售额', format: 'money' },
  { key: 'roi', label: 'ROI', format: 'money' },
  { key: 'cpa', label: 'CPA', format: 'money' },
  { key: 'avg_price', label: '单均价', format: 'money' },
  { key: 'session_reg_rate', label: '会话注册率', format: 'rate' },
  { key: 'cvr', label: 'CVR', format: 'rate' },
  { key: 'session_cart_rate', label: '会话加购率', format: 'rate' },
  { key: 'cart_conv_rate', label: '加购转化率', format: 'rate' },
];

const vooglamSessionExportColumns = (dimensions: VooglamSessionDimensionKey[]) => [
  ...dimensions.map(key => ({ key, label: VOOGLAM_SESSION_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key })),
  { key: 'spend', label: '账户花费' }, { key: 'clicks', label: '点击次数' }, { key: 'session_num', label: '会话数（神策）' },
  { key: 'arrival_rate', label: '到达率' }, { key: 'session_cost', label: '会话成本' }, { key: 'regist_user_num', label: '注册用户数' },
  { key: 'pdp_view_num', label: '商详页浏览PV' }, { key: 'add_cart_num', label: '加购成功PV' }, { key: 'order_num', label: '支付订单数' },
  { key: 'net_sales', label: '净销售额' }, { key: 'roi', label: '渠道ROI' }, { key: 'cpa', label: '渠道CPA' },
  { key: 'avg_price', label: '单均价' }, { key: 'session_reg_rate', label: '会话注册率' }, { key: 'cvr', label: 'CVR' },
  { key: 'session_cart_rate', label: '会话加购率' }, { key: 'cart_conv_rate', label: '加购转化率' },
];

const vooglamSessionComparisonExportColumns = [
  { key: 'label', label: '比较周期' },
  ...VOOGLAM_SESSION_COMPARISON_METRICS.map(metric => ({ key: metric.key, label: metric.label })),
];

function VooglamSessionComparisonTable({ rows, loading }: { rows: VooglamSessionComparisonRow[]; loading: boolean }) {
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const formatValue = (row: VooglamSessionComparisonRow, metric: typeof VOOGLAM_SESSION_COMPARISON_METRICS[number]) => {
    const formatter = row.is_growth || metric.format === 'rate'
      ? fmtPct
      : metric.format === 'integer' ? fmtInt : fmtNum;
    return formatSiteMetric(metric.key, row[metric.key], formatter, userLevel);
  };
  return (
    <div className="table-wrap" style={{ position: 'relative', maxHeight: 340, overflow: 'auto', marginBottom: 12 }}>
      <table style={{ minWidth: 1440 }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <tr>
            <th style={thBase} />
            {VOOGLAM_SESSION_COMPARISON_METRICS.map(metric => <th key={metric.key} style={{ ...thBase, ...tdNum }}><MetricDescriptionTooltip label={metric.label} /></th>)}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? <tr><td colSpan={VOOGLAM_SESSION_COMPARISON_METRICS.length + 1}><TableLoadingCell /></td></tr>
            : rows.length === 0 ? <tr><td colSpan={VOOGLAM_SESSION_COMPARISON_METRICS.length + 1} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>当前筛选范围暂无对比数据，请调整日期或渠道筛选。</td></tr>
            : rows.map(row => (
                <tr key={row.label} style={row.is_growth ? { background: 'var(--primary-bg)' } : undefined}>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{row.label}</td>
                  {VOOGLAM_SESSION_COMPARISON_METRICS.map(metric => <td key={metric.key} style={tdNum}>{formatValue(row, metric)}</td>)}
                </tr>
              ))}
        </tbody>
      </table>
      {loading && rows.length > 0 && <TableLoadingOverlay />}
    </div>
  );
}

// 通用表格
function DataTable<T>({ cols, rows, loading, dimCols, pageSize: defaultPageSize, summary }: {
  cols: { key: keyof T; label: string; fmt: (v: any) => string }[];
  rows: T[]; loading: boolean;
  dimCols: { key: keyof T; label: string }[];
  pageSize?: number;
  summary?: { label: string; values: Partial<T> } | null;
}) {
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const total = cols.length + dimCols.length;
  const tableRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const defaultDateSortKey = dimCols
    .map(column => String(column.key))
    .find(key => key === 'date' || key === 'period') ?? null;
  const previousDefaultDateSortKey = useRef(defaultDateSortKey);
  const { sort, toggleSort, sortedRows, setSort } = useTableSort<T, string>(
    rows,
    (row, key) => row[key as keyof T] as SortValue,
    defaultDateSortKey ? { key: defaultDateSortKey, direction: 'asc' } : null,
  );
  const totalPages = pageSize ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const visibleRows = pageSize
    ? sortedRows.slice((page - 1) * pageSize, page * pageSize)
    : sortedRows;

  const scrollTableToTop = useCallback(() => {
    tableRef.current?.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    setPage(1);
    scrollTableToTop();
  }, [rows, pageSize, scrollTableToTop]);

  useEffect(() => {
    setPageSize(defaultPageSize);
  }, [defaultPageSize]);

  useEffect(() => {
    if (previousDefaultDateSortKey.current === defaultDateSortKey) return;
    previousDefaultDateSortKey.current = defaultDateSortKey;
    setSort(defaultDateSortKey ? { key: defaultDateSortKey, direction: 'asc' } : null);
  }, [defaultDateSortKey, setSort]);

  const handleSort = useCallback((key: string) => {
    toggleSort(key);
    setPage(1);
    scrollTableToTop();
  }, [scrollTableToTop, toggleSort]);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
    scrollTableToTop();
  }, [scrollTableToTop]);

  return (
    <>
      <div ref={tableRef} className="table-wrap" style={{ position: 'relative', maxHeight: 460, overflow: 'auto' }}>
        <table style={{ minWidth: 200 + total * 90 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              {dimCols.map(d => <SortableHeader key={String(d.key)} sortKey={String(d.key)} sort={sort} onSort={handleSort} style={thBase}>{d.label}</SortableHeader>)}
              {cols.map(c => <SortableHeader key={String(c.key)} sortKey={String(c.key)} sort={sort} onSort={handleSort} align="right" disabled={isSiteSensitiveMetric(String(c.key)) && userLevel < 2} style={{ ...thBase, ...tdNum }}><MetricDescriptionTooltip label={c.label} /></SortableHeader>)}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? <tr><td colSpan={total}><TableLoadingCell /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={total} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
              : <>
                  {summary && (
                    <tr style={{ fontWeight: 700, background: 'var(--surface2)' }}>
                      {dimCols.map((d, index) => <td key={String(d.key)} style={{ whiteSpace: 'nowrap' }}>{index === 0 ? summary.label : '-'}</td>)}
                      {cols.map(c => <td key={String(c.key)} style={tdNum}>{formatSiteMetric(String(c.key), Number(summary.values[c.key] ?? 0), c.fmt, userLevel)}</td>)}
                    </tr>
                  )}
                  {visibleRows.map((r, i) => (
                    <tr key={(page - 1) * (pageSize ?? rows.length) + i}>
                      {dimCols.map(d => <td key={String(d.key)} style={{ whiteSpace: 'nowrap' }}>{String(r[d.key] ?? '-')}</td>)}
                      {cols.map(c => <td key={String(c.key)} style={tdNum}>{formatSiteMetric(String(c.key), Number(r[c.key]), c.fmt, userLevel)}</td>)}
                    </tr>
                  ))}
                </>}
          </tbody>
        </table>
        {loading && rows.length > 0 && <TableLoadingOverlay />}
      </div>
      {pageSize && rows.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onChange={handlePageChange}
          total={`共 ${rows.length} 条`}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setPage(1); scrollTableToTop(); }}
          disabled={loading}
        />
      )}
    </>
  );
}

export default function SiteChannelPortal() {
  const { shopName: site, brand, region } = useSiteShopName();
  const isZeeloolRegionalPortal = brand.toLowerCase() === 'zeelool' && ZEELOOL_REGIONAL_PORTAL_REGIONS.has(region.toLowerCase());
  const isVooglamPortal = brand.toLowerCase() === 'vooglam' || /^vooglam(?:_|$)/i.test(site);
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const sessionSection = useLazySection(Boolean(site));
  const userSection = useLazySection(Boolean(site));
  const payUserSection = useLazySection(Boolean(site));
  const productSection = useLazySection(Boolean(site));
  const vooglamSessionSection = useLazySection(Boolean(site && isVooglamPortal));
  const dayInit = rangeFor('day');
  const weekInit = lastCompletedWeekRange();

  const [coreStartDate, setCoreStartDate] = useState(dayInit.end);
  const [coreEndDate, setCoreEndDate] = useState(dayInit.end);
  const [coreQuickKey, setCoreQuickKey] = useState('yesterday');
  const [coreChannels, setCoreChannels] = useState('');
  const [coreTwoLevel, setCoreTwoLevel] = useState('');
  const [coreExclusions, setCoreExclusions] = useState<PortalFilterExclusions>({});

  const [trendStartDate, setTrendStartDate] = useState(dayInit.start);
  const [trendEndDate, setTrendEndDate] = useState(dayInit.end);
  const [trendQuickKey, setTrendQuickKey] = useState('recent7');
  const [trendChannels, setTrendChannels] = useState('');
  const [trendTwoLevel, setTrendTwoLevel] = useState('');
  const [trendExclusions, setTrendExclusions] = useState<PortalFilterExclusions>({});
  const [trendValueMetricKeys, setTrendValueMetricKeys] = useState<PortalTrendMetricKey[]>(DEFAULT_TREND_VALUE_KEYS);
  const [trendRatioMetricKeys, setTrendRatioMetricKeys] = useState<PortalTrendMetricKey[]>(DEFAULT_TREND_RATIO_KEYS);

  const [sessionPeriod, setSessionPeriod] = useState<PortalPeriod>('day');
  const [sessionStartDate, setSessionStartDate] = useState(dayInit.start);
  const [sessionEndDate, setSessionEndDate] = useState(dayInit.end);
  const [sessionQuickKey, setSessionQuickKey] = useState('recent7');
  const [sessionChannels, setSessionChannels] = useState(DEFAULT_SESSION_CHANNELS);
  const [sessionTwoLevel, setSessionTwoLevel] = useState('');
  const [sessionThreeLevel, setSessionThreeLevel] = useState('');
  const [sessionCountry, setSessionCountry] = useState('');
  const [sessionDevice, setSessionDevice] = useState('');
  const [sessionSource, setSessionSource] = useState('');
  const [sessionMedium, setSessionMedium] = useState('');
  const [sessionCampaign, setSessionCampaign] = useState('');
  const [sessionDimensions, setSessionDimensions] = useState<PortalSessionDimensionKey[]>(DEFAULT_SESSION_DIMENSIONS);
  const [sessionExclusions, setSessionExclusions] = useState<PortalFilterExclusions>({});

  const [userPeriod, setUserPeriod] = useState<PortalPeriod>('day');
  const [userStartDate, setUserStartDate] = useState(dayInit.end);
  const [userEndDate, setUserEndDate] = useState(dayInit.end);
  const [userChannels, setUserChannels] = useState(DEFAULT_CHANNELS);
  const [userTwoLevel, setUserTwoLevel] = useState('');
  const [userThreeLevel, setUserThreeLevel] = useState('');
  const [userCountry, setUserCountry] = useState('');
  const [userDevice, setUserDevice] = useState('');
  const [userDimensions, setUserDimensions] = useState<PortalUserDimensionKey[]>(DEFAULT_USER_DIMENSIONS);
  const [userExclusions, setUserExclusions] = useState<PortalFilterExclusions>({});

  const [payUserPeriod, setPayUserPeriod] = useState<Extract<PortalPeriod, 'week' | 'month'>>('week');
  const [payUserStartDate, setPayUserStartDate] = useState(weekInit.start);
  const [payUserEndDate, setPayUserEndDate] = useState(weekInit.end);
  const [payUserChannels, setPayUserChannels] = useState(DEFAULT_CHANNELS);
  const [payUserTwoLevel, setPayUserTwoLevel] = useState('');
  const [payUserThreeLevel, setPayUserThreeLevel] = useState('');
  const [payUserCountry, setPayUserCountry] = useState('');
  const [payUserDevice, setPayUserDevice] = useState('');
  const [payUserDimensions, setPayUserDimensions] = useState<PortalPayUserDimensionKey[]>(DEFAULT_PAY_USER_DIMENSIONS);
  const [payUserExclusions, setPayUserExclusions] = useState<PortalFilterExclusions>({});

  const [productPeriod, setProductPeriod] = useState<PortalPeriod>('day');
  const [productStartDate, setProductStartDate] = useState(dayInit.start);
  const [productEndDate, setProductEndDate] = useState(dayInit.end);
  const [productQuickKey, setProductQuickKey] = useState('recent7');
  const [productChannels, setProductChannels] = useState(DEFAULT_CHANNELS);
  const [productTwoLevel, setProductTwoLevel] = useState('');
  const [productThreeLevel, setProductThreeLevel] = useState('');
  const [fullNames, setFullNames] = useState<string[]>(['镜框/光学镜']);
  const [productSku, setProductSku] = useState('');
  const [businessSku, setBusinessSku] = useState('');
  const [shopSku, setShopSku] = useState('');
  const [productDimensions, setProductDimensions] = useState<PortalProductDimensionKey[]>(DEFAULT_PRODUCT_DIMENSIONS);
  const [productExclusions, setProductExclusions] = useState<PortalFilterExclusions>({});

  const [vooglamSessionPeriod, setVooglamSessionPeriod] = useState<PortalPeriod>('day');
  const [vooglamSessionStartDate, setVooglamSessionStartDate] = useState(dayInit.end);
  const [vooglamSessionEndDate, setVooglamSessionEndDate] = useState(dayInit.end);
  const [vooglamSessionQuickKey, setVooglamSessionQuickKey] = useState('yesterday');
  const [vooglamSessionTwoLevel, setVooglamSessionTwoLevel] = useState('');
  const [vooglamSessionThreeLevel, setVooglamSessionThreeLevel] = useState('');
  const [vooglamSessionDimensions, setVooglamSessionDimensions] = useState<VooglamSessionDimensionKey[]>(DEFAULT_VOOGLAM_SESSION_DIMENSIONS);


  const [core, setCore] = useState<PortalCoreCard[]>([]);
  const [trend, setTrend] = useState<PortalTrendRow[]>([]);
  const [session, setSession] = useState<PortalSessionRow[]>([]);
  const [user, setUser] = useState<PortalUserRow[]>([]);
  const [payUser, setPayUser] = useState<PortalPayUserRow[]>([]);
  const [product, setProduct] = useState<PortalProductRow[]>([]);
  const [vooglamSession, setVooglamSession] = useState<VooglamPortalSessionMultidimRow[]>([]);
  const [vooglamSessionComparison, setVooglamSessionComparison] = useState<VooglamSessionComparisonRow[]>([]);
  const [loading, setLoading] = useState<Record<PortalSection, boolean>>(emptyPortalLoading);
  const [error, setError] = useState('');
  const [queryVersions, setQueryVersions] = useState<Record<PortalSection, number>>(emptyPortalQueryVersions);
  const [exportSnapshots, setExportSnapshots] = useState<Partial<Record<PortalSection, PortalExportSnapshot>>>({});

  const requestVersions = useRef<Record<PortalSection, number>>({ ...emptyPortalQueryVersions });
  const requestSite = useRef(site);
  requestSite.current = site;
  useEffect(() => { setLoading({ ...emptyPortalLoading }); setExportSnapshots({}); }, [site]);
  useEffect(() => () => {
    Object.keys(requestVersions.current).forEach(key => { requestVersions.current[key as PortalSection] += 1; });
  }, []);

  const applyPeriod = (
    p: PortalPeriod,
    setP: (value: PortalPeriod) => void,
    setStart: (value: string) => void,
    setEnd: (value: string) => void,
    setQuick?: (value: string) => void,
  ) => {
    setP(p);
    const r = rangeFor(p);
    setStart(r.start);
    setEnd(r.end);
    setQuick?.('');
  };
  const applyQuickRange = (
    key: string,
    setQuick: (value: string) => void,
    setStart: (value: string) => void,
    setEnd: (value: string) => void,
    ranges = quickRanges(),
  ) => {
    if (key === 'custom') {
      setQuick(key);
      return;
    }
    const r = ranges.find(x => x.key === key);
    if (!r) return;
    setQuick(key);
    setStart(r.start);
    setEnd(r.end);
  };

  const coreParams = useMemo((): PortalParams => ({
    site, start_date: coreStartDate, end_date: coreEndDate, period: 'day', channels: coreChannels, two_level: coreTwoLevel, exclude: exclusionParam(coreExclusions),
  }), [site, coreStartDate, coreEndDate, coreChannels, coreTwoLevel, coreExclusions]);
  const trendParams = useMemo((): PortalParams => ({
    site, start_date: trendStartDate, end_date: trendEndDate, period: 'day', channels: trendChannels, two_level: trendTwoLevel, exclude: exclusionParam(trendExclusions),
  }), [site, trendStartDate, trendEndDate, trendChannels, trendTwoLevel, trendExclusions]);
  const visibleCore = useMemo(
    () => isZeeloolRegionalPortal ? core.filter(metric => !ZEELOOL_REGIONAL_HIDDEN_METRICS.has(metric.key)) : core,
    [core, isZeeloolRegionalPortal],
  );
  const visibleTrendValueMetrics = useMemo(
    () => isZeeloolRegionalPortal ? PORTAL_TREND_VALUE_METRICS.filter(metric => !ZEELOOL_REGIONAL_HIDDEN_METRICS.has(metric.key)) : PORTAL_TREND_VALUE_METRICS,
    [isZeeloolRegionalPortal],
  );
  const visibleTrendRatioMetrics = useMemo(
    () => isZeeloolRegionalPortal ? PORTAL_TREND_RATIO_METRICS.filter(metric => !ZEELOOL_REGIONAL_HIDDEN_METRICS.has(metric.key)) : PORTAL_TREND_RATIO_METRICS,
    [isZeeloolRegionalPortal],
  );
  const visibleTrendValueKeys = useMemo(
    () => trendValueMetricKeys.filter(key => !isZeeloolRegionalPortal || !ZEELOOL_REGIONAL_HIDDEN_METRICS.has(key)),
    [isZeeloolRegionalPortal, trendValueMetricKeys],
  );
  const visibleTrendRatioKeys = useMemo(
    () => trendRatioMetricKeys.filter(key => !isZeeloolRegionalPortal || !ZEELOOL_REGIONAL_HIDDEN_METRICS.has(key)),
    [isZeeloolRegionalPortal, trendRatioMetricKeys],
  );
  const trendExportColumns = useMemo(
    () => portalTrendExportColumns([...visibleTrendValueKeys, ...visibleTrendRatioKeys]),
    [visibleTrendRatioKeys, visibleTrendValueKeys],
  );
  const sessionSummary = useMemo(() => {
    if (session.length === 0) return null;
    const totals = session.reduce((acc, row) => {
      acc.sessionNum += row.session_num;
      acc.registUserNum += row.regist_user_num;
      acc.pdpViewNum += row.pdp_view_num;
      acc.addCartNum += row.add_cart_num;
      acc.orderNum += row.order_num;
      acc.netSales += row.net_sales;
      return acc;
    }, { sessionNum: 0, registUserNum: 0, pdpViewNum: 0, addCartNum: 0, orderNum: 0, netSales: 0 });
    return {
      label: '总计',
      values: {
        session_num: totals.sessionNum,
        regist_user_num: totals.registUserNum,
        pdp_view_num: totals.pdpViewNum,
        add_cart_num: totals.addCartNum,
        order_num: totals.orderNum,
        net_sales: totals.netSales,
        avg_price: totals.orderNum ? totals.netSales / totals.orderNum : 0,
        session_reg_rate: totals.sessionNum ? totals.registUserNum / totals.sessionNum : 0,
        cvr: totals.sessionNum ? totals.orderNum / totals.sessionNum : 0,
        session_cart_rate: totals.sessionNum ? totals.addCartNum / totals.sessionNum : 0,
        cart_conv_rate: totals.addCartNum ? totals.orderNum / totals.addCartNum : 0,
      } satisfies Partial<PortalSessionRow>,
    };
  }, [session]);
  const vooglamSessionSummary = useMemo(() => {
    if (vooglamSession.length === 0) return null;
    const totals = vooglamSession.reduce((acc, row) => {
      acc.spend += row.spend;
      acc.clicks += row.clicks;
      acc.sessions += row.session_num;
      acc.registrations += row.regist_user_num;
      acc.pdpViews += row.pdp_view_num;
      acc.addCarts += row.add_cart_num;
      acc.orders += row.order_num;
      acc.netSales += row.net_sales;
      return acc;
    }, { spend: 0, clicks: 0, sessions: 0, registrations: 0, pdpViews: 0, addCarts: 0, orders: 0, netSales: 0 });
    return {
      label: '总计',
      values: {
        spend: totals.spend,
        clicks: totals.clicks,
        session_num: totals.sessions,
        arrival_rate: totals.clicks ? totals.sessions / totals.clicks : 0,
        session_cost: totals.sessions ? totals.spend / totals.sessions : 0,
        regist_user_num: totals.registrations,
        pdp_view_num: totals.pdpViews,
        add_cart_num: totals.addCarts,
        order_num: totals.orders,
        net_sales: totals.netSales,
        roi: totals.spend ? totals.netSales / totals.spend : 0,
        cpa: totals.orders ? totals.spend / totals.orders : 0,
        avg_price: totals.orders ? totals.netSales / totals.orders : 0,
        session_reg_rate: totals.sessions ? totals.registrations / totals.sessions : 0,
        cvr: totals.sessions ? totals.orders / totals.sessions : 0,
        session_cart_rate: totals.sessions ? totals.addCarts / totals.sessions : 0,
        cart_conv_rate: totals.addCarts ? totals.orders / totals.addCarts : 0,
      } satisfies Partial<VooglamPortalSessionMultidimRow>,
    };
  }, [vooglamSession]);
  const sessionParams = useMemo((): PortalParams => ({
    site, start_date: sessionStartDate, end_date: sessionEndDate, period: sessionPeriod,
    channels: sessionChannels, two_level: sessionTwoLevel, three_level: sessionThreeLevel,
    country: sessionCountry, device: sessionDevice, source: sessionSource, medium: sessionMedium, campaign: sessionCampaign,
    dimensions: sessionDimensions.join(','), exclude: exclusionParam(sessionExclusions),
  }), [site, sessionStartDate, sessionEndDate, sessionPeriod, sessionChannels, sessionTwoLevel, sessionThreeLevel, sessionCountry, sessionDevice, sessionSource, sessionMedium, sessionCampaign, sessionDimensions, sessionExclusions]);
  const userParams = useMemo((): PortalParams => ({
    site, start_date: userStartDate, end_date: userEndDate, period: userPeriod,
    channels: userChannels, two_level: userTwoLevel, three_level: userThreeLevel, country: userCountry, device: userDevice,
    dimensions: userDimensions.join(','), exclude: exclusionParam(userExclusions),
  }), [site, userStartDate, userEndDate, userPeriod, userChannels, userTwoLevel, userThreeLevel, userCountry, userDevice, userDimensions, userExclusions]);
  const userDimensionColumns = useMemo(
    () => userDimensions.map(key => ({ key, label: PORTAL_USER_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key })),
    [userDimensions],
  );
  const userExportColumns = useMemo(() => portalUserExportColumns(userDimensions), [userDimensions]);
  const payUserParams = useMemo((): PortalParams => ({
    site, start_date: payUserStartDate, end_date: payUserEndDate, period: payUserPeriod,
    channels: payUserChannels, two_level: payUserTwoLevel, three_level: payUserThreeLevel, country: payUserCountry, device: payUserDevice, exclude: exclusionParam(payUserExclusions),
    dimensions: payUserDimensions.join(','),
  }), [site, payUserStartDate, payUserEndDate, payUserPeriod, payUserChannels, payUserTwoLevel, payUserThreeLevel, payUserCountry, payUserDevice, payUserDimensions, payUserExclusions]);
  const productParams = useMemo((): PortalParams => ({
    site, start_date: productStartDate, end_date: productEndDate, period: productPeriod,
    channels: productChannels, two_level: productTwoLevel, three_level: productThreeLevel,
    full_name: fullNames.join(','), product_sku: productSku, business_sku: businessSku, shop_sku: shopSku, dimensions: productDimensions.join(','), exclude: exclusionParam(productExclusions),
  }), [site, productStartDate, productEndDate, productPeriod, productChannels, productTwoLevel, productThreeLevel, fullNames, productSku, businessSku, shopSku, productDimensions, productExclusions]);
  const vooglamSessionParams = useMemo((): PortalParams => ({
    site, start_date: vooglamSessionStartDate, end_date: vooglamSessionEndDate, period: vooglamSessionPeriod,
    two_level: vooglamSessionTwoLevel, three_level: vooglamSessionThreeLevel, dimensions: vooglamSessionDimensions.join(','),
  }), [site, vooglamSessionStartDate, vooglamSessionEndDate, vooglamSessionPeriod, vooglamSessionTwoLevel, vooglamSessionThreeLevel, vooglamSessionDimensions]);
  const savedViewSnapshot: PortalViewSnapshot = {
    sections: {
      core: capturePortalViewSection(coreParams, coreQuickKey),
      trend: capturePortalViewSection(trendParams, trendQuickKey),
      session: capturePortalViewSection(sessionParams, sessionQuickKey),
      user: capturePortalViewSection(userParams),
      payUser: capturePortalViewSection(payUserParams),
      product: capturePortalViewSection(productParams, productQuickKey),
      ...(isVooglamPortal ? { vooglamSession: capturePortalViewSection(vooglamSessionParams, vooglamSessionQuickKey) } : {}),
    },
    trendValueMetricKeys, trendRatioMetricKeys,
  };

  const applySavedView = (snapshot: PortalViewSnapshot) => {
    if (!isPortalViewSnapshot(snapshot)) throw new Error('视图筛选不完整或日期无效，请重新保存');
    if (Boolean(snapshot.sections.vooglamSession) !== isVooglamPortal) throw new Error('该视图与当前品牌的报表范围不一致');
    const restoredExclusions = (value = ''): PortalFilterExclusions => Object.fromEntries(splitCsvValue(value).map(key => [key, true]));
    {
      const section = snapshot.sections.core;
      const p = section.params;
      setCoreStartDate(p.start_date); setCoreEndDate(p.end_date);
      setCoreQuickKey(section.quickKey === 'today' ? 'custom' : section.quickKey);
      setCoreChannels(p.channels ?? '');
      setCoreTwoLevel(p.two_level ?? '');
      setCoreExclusions(restoredExclusions(p.exclude));
    }
    {
      const section = snapshot.sections.trend;
      const p = section.params;
      setTrendStartDate(p.start_date); setTrendEndDate(p.end_date);
      setTrendQuickKey(section.quickKey === 'today' ? 'custom' : section.quickKey);
      setTrendChannels(p.channels ?? '');
      setTrendTwoLevel(p.two_level ?? '');
      setTrendExclusions(restoredExclusions(p.exclude));
    }
    {
      const section = snapshot.sections.session;
      const p = section.params;
      setSessionStartDate(p.start_date); setSessionEndDate(p.end_date);
      setSessionPeriod(p.period ?? 'day');
      setSessionQuickKey(section.quickKey === 'today' ? 'custom' : section.quickKey);
      setSessionChannels(p.channels ?? '');
      setSessionTwoLevel(p.two_level ?? '');
      setSessionThreeLevel(p.three_level ?? '');
      setSessionCountry(p.country ?? '');
      setSessionDevice(p.device ?? '');
      setSessionSource(p.source ?? '');
      setSessionMedium(p.medium ?? '');
      setSessionCampaign(p.campaign ?? '');
      setSessionExclusions(restoredExclusions(p.exclude));
      setSessionDimensions(fixedPortalDimensionOrder(splitCsvValue(p.dimensions ?? ''), PORTAL_SESSION_DIMENSIONS, DEFAULT_SESSION_DIMENSIONS));
    }
    {
      const section = snapshot.sections.user;
      const p = section.params;
      setUserStartDate(p.start_date); setUserEndDate(p.end_date);
      setUserPeriod(p.period ?? 'day');
      setUserChannels(p.channels ?? '');
      setUserTwoLevel(p.two_level ?? '');
      setUserThreeLevel(p.three_level ?? '');
      setUserCountry(p.country ?? '');
      setUserDevice(p.device ?? '');
      setUserExclusions(restoredExclusions(p.exclude));
      setUserDimensions(fixedPortalDimensionOrder(splitCsvValue(p.dimensions ?? ''), PORTAL_USER_DIMENSIONS, DEFAULT_USER_DIMENSIONS));
    }
    {
      const section = snapshot.sections.payUser;
      const p = section.params;
      setPayUserStartDate(p.start_date); setPayUserEndDate(p.end_date);
      setPayUserPeriod(p.period === 'month' ? 'month' : 'week');
      setPayUserChannels(p.channels ?? '');
      setPayUserTwoLevel(p.two_level ?? '');
      setPayUserThreeLevel(p.three_level ?? '');
      setPayUserCountry(p.country ?? '');
      setPayUserDevice(p.device ?? '');
      setPayUserExclusions(restoredExclusions(p.exclude));
      setPayUserDimensions(fixedPortalDimensionOrder(splitCsvValue(p.dimensions ?? ''), PORTAL_PAY_USER_DIMENSIONS, DEFAULT_PAY_USER_DIMENSIONS));
    }
    {
      const section = snapshot.sections.product;
      const p = section.params;
      setProductStartDate(p.start_date); setProductEndDate(p.end_date);
      setProductPeriod(p.period ?? 'day');
      setProductQuickKey(section.quickKey === 'today' ? 'custom' : section.quickKey);
      setProductChannels(p.channels ?? '');
      setProductTwoLevel(p.two_level ?? '');
      setProductThreeLevel(p.three_level ?? '');
      setProductExclusions(restoredExclusions(p.exclude));
      setProductDimensions(fixedPortalDimensionOrder(splitCsvValue(p.dimensions ?? ''), PORTAL_PRODUCT_DIMENSIONS, DEFAULT_PRODUCT_DIMENSIONS));
      setFullNames(splitCsvValue(p.full_name ?? ''));
      setProductSku(p.product_sku ?? ''); setBusinessSku(p.business_sku ?? ''); setShopSku(p.shop_sku ?? '');
    }
    if (snapshot.sections.vooglamSession) {
      const section = snapshot.sections.vooglamSession;
      const p = section.params;
      setVooglamSessionStartDate(p.start_date); setVooglamSessionEndDate(p.end_date);
      setVooglamSessionPeriod(p.period ?? 'day');
      setVooglamSessionQuickKey(section.quickKey === 'today' ? 'custom' : section.quickKey);
      setVooglamSessionTwoLevel(p.two_level ?? '');
      setVooglamSessionThreeLevel(p.three_level ?? '');
      setVooglamSessionDimensions(fixedPortalDimensionOrder(splitCsvValue(p.dimensions ?? ''), VOOGLAM_SESSION_DIMENSIONS, DEFAULT_VOOGLAM_SESSION_DIMENSIONS));
    }
    setTrendValueMetricKeys(PORTAL_TREND_VALUE_METRICS.filter(metric => snapshot.trendValueMetricKeys.includes(metric.key)).map(metric => metric.key));
    setTrendRatioMetricKeys(PORTAL_TREND_RATIO_METRICS.filter(metric => snapshot.trendRatioMetricKeys.includes(metric.key)).map(metric => metric.key));
    // 同一次 React 更新中恢复全部筛选，再触发各区块查询；旧请求不能覆盖新视图。
    Object.keys(requestVersions.current).forEach(key => { requestVersions.current[key as PortalSection] += 1; });
    setExportSnapshots({});
    setQueryVersions(versions => Object.fromEntries(Object.entries(versions).map(([key, value]) => [key, value + 1])) as Record<PortalSection, number>);
  };

  const setSectionLoading = useCallback((section: PortalSection, value: boolean) => {
    setLoading(prev => ({ ...prev, [section]: value }));
  }, []);

  const fetchCore = useCallback(async () => {
    if (!site) return;
    const version = ++requestVersions.current.core;
    const isCurrent = () => requestVersions.current.core === version && requestSite.current === site;
    setSectionLoading('core', true);
    setExportSnapshots(current => ({ ...current, core: undefined }));
    setError('');
    try {
      const co = await siteDataApi.getPortalCore(coreParams);
      if (!isCurrent()) return;
      setCore(co.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, core: { params: { ...coreParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setCore([]);
      setExportSnapshots(current => ({ ...current, core: undefined }));
      setError(e instanceof Error ? `核心指标加载失败：${e.message}` : '核心指标加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('core', false);
    }
  }, [site, coreParams, setSectionLoading]);

  const fetchTrend = useCallback(async () => {
    if (!site) return;
    const version = ++requestVersions.current.trend;
    const isCurrent = () => requestVersions.current.trend === version && requestSite.current === site;
    setSectionLoading('trend', true);
    setExportSnapshots(current => ({ ...current, trend: undefined }));
    setError('');
    try {
      const tr = await siteDataApi.getPortalTrend(trendParams);
      if (!isCurrent()) return;
      setTrend(tr.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, trend: { params: { ...trendParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setTrend([]);
      setExportSnapshots(current => ({ ...current, trend: undefined }));
      setError(e instanceof Error ? `核心指标趋势加载失败：${e.message}` : '核心指标趋势加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('trend', false);
    }
  }, [site, trendParams, setSectionLoading]);

  const fetchSession = useCallback(async () => {
    if (!site) return;
    const version = ++requestVersions.current.session;
    const isCurrent = () => requestVersions.current.session === version && requestSite.current === site;
    setSectionLoading('session', true);
    setExportSnapshots(current => ({ ...current, session: undefined }));
    setError('');
    try {
      const se = await siteDataApi.getPortalSession(sessionParams);
      if (!isCurrent()) return;
      setSession(se.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, session: { params: { ...sessionParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setSession([]);
      setExportSnapshots(current => ({ ...current, session: undefined }));
      setError(e instanceof Error ? `会话加载失败：${e.message}` : '会话加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('session', false);
    }
  }, [site, sessionParams, setSectionLoading]);

  const fetchUser = useCallback(async () => {
    if (!site) return;
    const version = ++requestVersions.current.user;
    const isCurrent = () => requestVersions.current.user === version && requestSite.current === site;
    setSectionLoading('user', true);
    setExportSnapshots(current => ({ ...current, user: undefined }));
    setError('');
    try {
      const us = await siteDataApi.getPortalUser(userParams);
      if (!isCurrent()) return;
      setUser(us.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, user: { params: { ...userParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setUser([]);
      setExportSnapshots(current => ({ ...current, user: undefined }));
      setError(e instanceof Error ? `用户加载失败：${e.message}` : '用户加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('user', false);
    }
  }, [site, userParams, setSectionLoading]);

  const fetchPayUser = useCallback(async () => {
    if (!site) return;
    const version = ++requestVersions.current.payUser;
    const isCurrent = () => requestVersions.current.payUser === version && requestSite.current === site;
    setSectionLoading('payUser', true);
    setExportSnapshots(current => ({ ...current, payUser: undefined }));
    setError('');
    try {
      const pu = await siteDataApi.getPortalPayUser(payUserParams);
      if (!isCurrent()) return;
      setPayUser(pu.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, payUser: { params: { ...payUserParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setPayUser([]);
      setExportSnapshots(current => ({ ...current, payUser: undefined }));
      setError(e instanceof Error ? `支付用户数加载失败：${e.message}` : '支付用户数加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('payUser', false);
    }
  }, [site, payUserParams, setSectionLoading]);

  const fetchProduct = useCallback(async () => {
    if (!site) return;
    const version = ++requestVersions.current.product;
    const isCurrent = () => requestVersions.current.product === version && requestSite.current === site;
    setSectionLoading('product', true);
    setExportSnapshots(current => ({ ...current, product: undefined }));
    setError('');
    try {
      const pr = await siteDataApi.getPortalProduct(productParams);
      if (!isCurrent()) return;
      setProduct(pr.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, product: { params: { ...productParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setProduct([]);
      setExportSnapshots(current => ({ ...current, product: undefined }));
      setError(e instanceof Error ? `商品加载失败：${e.message}` : '商品加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('product', false);
    }
  }, [site, productParams, setSectionLoading]);

  const fetchVooglamSession = useCallback(async () => {
    if (!site || !isVooglamPortal) return;
    const version = ++requestVersions.current.vooglamSession;
    const isCurrent = () => requestVersions.current.vooglamSession === version && requestSite.current === site;
    setSectionLoading('vooglamSession', true);
    setExportSnapshots(current => ({ ...current, vooglamSession: undefined }));
    setError('');
    try {
      const response = await siteDataApi.getVooglamPortalSessionMultidim(vooglamSessionParams);
      if (!isCurrent()) return;
      setVooglamSession(response.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, vooglamSession: { params: { ...vooglamSessionParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setVooglamSession([]);
      setExportSnapshots(current => ({ ...current, vooglamSession: undefined }));
      setError(e instanceof Error ? `Vooglam 会话多维加载失败：${e.message}` : 'Vooglam 会话多维加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('vooglamSession', false);
    }
  }, [isVooglamPortal, site, setSectionLoading, vooglamSessionParams]);

  const fetchVooglamSessionComparison = useCallback(async () => {
    if (!site || !isVooglamPortal) return;
    const version = ++requestVersions.current.vooglamComparison;
    const isCurrent = () => requestVersions.current.vooglamComparison === version && requestSite.current === site;
    setSectionLoading('vooglamComparison', true);
    setExportSnapshots(current => ({ ...current, vooglamComparison: undefined }));
    try {
      const response = await siteDataApi.getVooglamPortalSessionComparison(vooglamSessionParams);
      if (!isCurrent()) return;
      setVooglamSessionComparison(response.data?.list ?? []);
      setExportSnapshots(current => ({ ...current, vooglamComparison: { params: { ...vooglamSessionParams } } }));
    } catch (e) {
      if (!isCurrent()) return;
      setVooglamSessionComparison([]);
      setExportSnapshots(current => ({ ...current, vooglamComparison: undefined }));
      setError(e instanceof Error ? `Vooglam 会话对比加载失败：${e.message}` : 'Vooglam 会话对比加载失败');
    } finally {
      if (isCurrent()) setSectionLoading('vooglamComparison', false);
    }
  }, [isVooglamPortal, site, setSectionLoading, vooglamSessionParams]);

  useEffect(() => { if (site) fetchCore(); }, [queryVersions.core, site]);
  useEffect(() => { if (site) fetchTrend(); }, [queryVersions.trend, site]);
  useEffect(() => { if (site && sessionSection.ready) fetchSession(); }, [queryVersions.session, sessionSection.ready, site]);
  useEffect(() => { if (site && userSection.ready) fetchUser(); }, [queryVersions.user, site, userSection.ready]);
  useEffect(() => { if (site && payUserSection.ready) fetchPayUser(); }, [payUserSection.ready, queryVersions.payUser, site]);
  useEffect(() => { if (site && productSection.ready) fetchProduct(); }, [productSection.ready, queryVersions.product, site]);
  useEffect(() => { if (site && isVooglamPortal && vooglamSessionSection.ready) fetchVooglamSession(); }, [isVooglamPortal, queryVersions.vooglamSession, site, vooglamSessionSection.ready]);
  useEffect(() => { if (site && isVooglamPortal && vooglamSessionSection.ready) fetchVooglamSessionComparison(); }, [isVooglamPortal, queryVersions.vooglamComparison, site, vooglamSessionSection.ready]);

  const querySection = (section: PortalSection) => {
    setQueryVersions(versions => ({
      ...versions,
      [section]: versions[section] + 1,
      ...(section === 'vooglamSession' ? { vooglamComparison: versions.vooglamComparison + 1 } : {}),
    }));
  };

  const resetSection = (section: Exclude<PortalSection, 'vooglamComparison'>) => {
    switch (section) {
      case 'core':
        setCoreStartDate(dayInit.end); setCoreEndDate(dayInit.end); setCoreQuickKey('yesterday'); setCoreChannels(''); setCoreTwoLevel(''); setCoreExclusions({});
        break;
      case 'trend':
        setTrendStartDate(dayInit.start); setTrendEndDate(dayInit.end); setTrendQuickKey('recent7'); setTrendChannels(''); setTrendTwoLevel(''); setTrendExclusions({}); setTrendValueMetricKeys(DEFAULT_TREND_VALUE_KEYS); setTrendRatioMetricKeys(DEFAULT_TREND_RATIO_KEYS);
        break;
      case 'session':
        setSessionPeriod('day'); setSessionStartDate(dayInit.start); setSessionEndDate(dayInit.end); setSessionQuickKey('recent7'); setSessionChannels(DEFAULT_SESSION_CHANNELS); setSessionTwoLevel(''); setSessionThreeLevel(''); setSessionCountry(''); setSessionDevice(''); setSessionSource(''); setSessionMedium(''); setSessionCampaign(''); setSessionDimensions(DEFAULT_SESSION_DIMENSIONS); setSessionExclusions({});
        break;
      case 'user':
        setUserPeriod('day'); setUserStartDate(dayInit.end); setUserEndDate(dayInit.end); setUserChannels(DEFAULT_CHANNELS); setUserTwoLevel(''); setUserThreeLevel(''); setUserCountry(''); setUserDevice(''); setUserDimensions(DEFAULT_USER_DIMENSIONS); setUserExclusions({});
        break;
      case 'payUser':
        setPayUserPeriod('week'); setPayUserStartDate(weekInit.start); setPayUserEndDate(weekInit.end); setPayUserChannels(DEFAULT_CHANNELS); setPayUserTwoLevel(''); setPayUserThreeLevel(''); setPayUserCountry(''); setPayUserDevice(''); setPayUserDimensions(DEFAULT_PAY_USER_DIMENSIONS); setPayUserExclusions({});
        break;
      case 'product':
        setProductPeriod('day'); setProductStartDate(dayInit.start); setProductEndDate(dayInit.end); setProductQuickKey('recent7'); setProductChannels(DEFAULT_CHANNELS); setProductTwoLevel(''); setProductThreeLevel(''); setFullNames(['镜框/光学镜']); setProductSku(''); setBusinessSku(''); setShopSku(''); setProductDimensions(DEFAULT_PRODUCT_DIMENSIONS); setProductExclusions({});
        break;
      case 'vooglamSession':
        setVooglamSessionPeriod('day'); setVooglamSessionStartDate(dayInit.end); setVooglamSessionEndDate(dayInit.end); setVooglamSessionQuickKey('yesterday'); setVooglamSessionTwoLevel(''); setVooglamSessionThreeLevel(''); setVooglamSessionDimensions(DEFAULT_VOOGLAM_SESSION_DIMENSIONS);
        break;
    }
    querySection(section);
  };

  const renderPeriodTabs = (value: PortalPeriod, onChange: (value: PortalPeriod) => void, periods = ALL_PERIODS) => (
    <div className="page-tabs" style={{ marginBottom: 0 }}>
      {periods.map(t => (
        <div key={t.key} className={`page-tab${value === t.key ? ' active' : ''}`} onClick={() => onChange(t.key)}>{t.label}</div>
      ))}
    </div>
  );

  const renderQuickTabs = (
    value: string,
    onChange: (key: string) => void,
    ranges = quickRanges(),
  ) => (
    <div className="page-tabs" style={{ marginBottom: 0 }}>
      {ranges.map(r => (
        <div key={r.key} className={`page-tab${value === r.key ? ' active' : ''}`} onClick={() => onChange(r.key)}>{r.label}</div>
      ))}
    </div>
  );

  const renderDateRow = ({
    period,
    setPeriod,
    start,
    end,
    setStart,
    setEnd,
    quickKey,
    setQuickKey,
    onPeriodChange,
    periods = ALL_PERIODS,
    quickItems = quickRanges(),
  }: {
    period?: PortalPeriod;
    setPeriod?: (value: PortalPeriod) => void;
    start: string;
    end: string;
    setStart: (value: string) => void;
    setEnd: (value: string) => void;
    quickKey?: string;
    setQuickKey?: (value: string) => void;
    onPeriodChange?: (value: PortalPeriod) => void;
    periods?: { key: PortalPeriod; label: string }[];
    quickItems?: { key: string; label: string; start: string; end: string }[];
  }) => (
    <div className="data-filter-row time">
      {period && setPeriod && (
        <>
          <span className="data-filter-inline-label">日期维度</span>
          {renderPeriodTabs(period, p => {
            if (onPeriodChange) {
              onPeriodChange(p);
            } else {
              applyPeriod(p, setPeriod, setStart, setEnd, setQuickKey);
            }
          }, periods)}
        </>
      )}
      {quickKey !== undefined && setQuickKey && (
        <>
          <span className="data-filter-inline-label">日期</span>
          {renderQuickTabs(quickKey, key => applyQuickRange(key, setQuickKey, setStart, setEnd, quickItems), quickItems)}
        </>
      )}
      <DateRangeInputs grain={period ?? 'day'} start={start} end={end} onStart={value => { setStart(value); setQuickKey?.('custom'); }} onEnd={value => { setEnd(value); setQuickKey?.('custom'); }} />
    </div>
  );

  const renderChannelInputs = ({
    channels,
    setChannels,
    twoLevel,
    setTwoLevel,
    threeLevel,
    setThreeLevel,
    country,
    setCountry,
    device,
    setDevice,
    source,
    setSource,
    medium,
    setMedium,
    campaign,
    setCampaign,
    exclusions,
    setExclusions,
    includeThird = true,
    includeCountry = true,
    includeDevice = true,
    includeUtm = false,
  }: {
    channels: string;
    setChannels: (value: string) => void;
    twoLevel: string;
    setTwoLevel: (value: string) => void;
    threeLevel?: string;
    setThreeLevel?: (value: string) => void;
    country?: string;
    setCountry?: (value: string) => void;
    device?: string;
    setDevice?: (value: string) => void;
    source?: string;
    setSource?: (value: string) => void;
    medium?: string;
    setMedium?: (value: string) => void;
    campaign?: string;
    setCampaign?: (value: string) => void;
    exclusions: PortalFilterExclusions;
    setExclusions: Dispatch<SetStateAction<PortalFilterExclusions>>;
    includeThird?: boolean;
    includeCountry?: boolean;
    includeDevice?: boolean;
    includeUtm?: boolean;
  }) => (
    <div className="data-filter-row compact">
      <PortalMultiSelectFilter
        label="一级渠道"
        field="one_level_channel"
        value={channels}
        onChange={setChannels}
        exclude={Boolean(exclusions.channels)}
        onExcludeChange={exclude => setExclusions(prev => ({ ...prev, channels: exclude }))}
        size="lg"
      />
      <PortalSelectFilter
        label="二级渠道"
        field="two_level_channel"
        value={twoLevel}
        onChange={setTwoLevel}
        exclude={Boolean(exclusions.two_level)}
        onExcludeChange={exclude => setExclusions(prev => ({ ...prev, two_level: exclude }))}
        size="lg"
      />
      {includeThird && setThreeLevel && (
        <PortalSelectFilter
          label="三级渠道"
          field="three_level_channel"
          value={threeLevel ?? ''}
          onChange={setThreeLevel}
          exclude={Boolean(exclusions.three_level)}
          onExcludeChange={exclude => setExclusions(prev => ({ ...prev, three_level: exclude }))}
          size="lg"
        />
      )}
      {includeDevice && setDevice && (
        <PortalSelectFilter
          label="终端"
          field="store_name"
          value={device ?? ''}
          onChange={setDevice}
          exclude={Boolean(exclusions.device)}
          onExcludeChange={exclude => setExclusions(prev => ({ ...prev, device: exclude }))}
          size="sm"
        />
      )}
      {includeCountry && setCountry && (
        <PortalSelectFilter
          label="国家"
          field="country"
          value={country ?? ''}
          onChange={setCountry}
          exclude={Boolean(exclusions.country)}
          onExcludeChange={exclude => setExclusions(prev => ({ ...prev, country: exclude }))}
          size="sm"
        />
      )}
      {includeUtm && setSource && (
        <PortalSelectFilter
          label="来源"
          field="pub_zl_utm_source"
          value={source ?? ''}
          onChange={setSource}
          exclude={Boolean(exclusions.source)}
          onExcludeChange={exclude => setExclusions(prev => ({ ...prev, source: exclude }))}
        />
      )}
      {includeUtm && setMedium && (
        <PortalMultiSelectFilter
          label="媒介"
          field="pub_zl_utm_medium"
          value={medium ?? ''}
          onChange={setMedium}
          exclude={Boolean(exclusions.medium)}
          onExcludeChange={exclude => setExclusions(prev => ({ ...prev, medium: exclude }))}
        />
      )}
      {includeUtm && setCampaign && <TextFilter label="Campaign" value={campaign ?? ''} onChange={setCampaign} placeholder="不限" size="xl" />}
    </div>
  );

  const renderQueryActions = (section: Exclude<PortalSection, 'vooglamComparison'>) => (
    <div className="data-filter-row" style={{ marginTop: 12 }}>
      <DashboardQueryActions loading={loading[section]} onReset={() => resetSection(section)} onQuery={() => querySection(section)} />
    </div>
  );

  return (
    <div className="page-section visible">
      <ChannelPortalSavedViews snapshot={savedViewSnapshot} onApply={applySavedView} loading={Object.values(loading).some(Boolean)} />
      {error && <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>{error}</div>}

      <aside className="data-update-note" aria-labelledby="channel-portal-data-note-title">
        <h2 id="channel-portal-data-note-title">数据更新说明</h2>
        <div className="data-update-note-content">
          <p>1、用户营销等渠道的花费数据依赖业务同学每天 10 点前在钉钉文档中更新前一天的花费数据，当前看板中的花费以及花费相关数据（如CPA）会在中午 13 点以后产出（如果当天 10 点前没有更新前一天的花费数据，这些渠道的花费及花费相关数据当天就不会更新）</p>
          <p>2、付费广告的数据会在每天 8 点前在看板中完成更新</p>
        </div>
      </aside>

      {isVooglamPortal && (
        <aside className="data-update-note" aria-labelledby="vooglam-channel-portal-note-title" style={{ marginTop: -4 }}>
          <h2 id="vooglam-channel-portal-note-title" style={{ color: 'var(--red)' }}>注意</h2>
          <div className="data-update-note-content">
            <p>会话数和销售指标（支付订单数、净销售额等）自 2024 年 11 月起有分流量类型数据；流量指标（商详浏览次数、加购次数等）自 2025 年 7 月 15 日起有分流量类型数据。</p>
            <p>渠道花费取决于业务侧更新；业务侧未更新时，报表无花费数据，也会影响会话成本、CPA、ROI 等相关指标。会话及流量相关数据自 2025 年 4 月 1 日起剔除了爬虫数据，流量类型筛选自 2025 年 7 月 15 日起有值。</p>
            <p>流量类型释义：UP、覆盖为提升知名度和互动；浏览为网页/落地页浏览、链接点击等；注册为提升注册；BOT、加购为促进加购；购买（品牌）为品牌侧购买标识；其他为非以上类型。</p>
          </div>
        </aside>
      )}

      {/* 核心指标卡 */}
      <Card
        title={`核心指标（${coreStartDate === coreEndDate ? coreEndDate : `${coreStartDate} 至 ${coreEndDate}`}）`}
        extra={<ReportExportButton reportKey="site_channel_portal_core" title="渠道门户-核心指标" fileName={`渠道门户_核心指标_${exportSnapshots.core?.params.end_date ?? ''}.csv`} params={exportSnapshots.core?.params ?? {}} columns={portalCoreExportColumns} disabled={!exportSnapshots.core || loading.core} />}
        allowOverflow
      >
        <div className="data-filter-card" style={{ margin: 16, marginBottom: 0 }}>
          {renderDateRow({
            start: coreStartDate,
            end: coreEndDate,
            setStart: setCoreStartDate,
            setEnd: setCoreEndDate,
            quickKey: coreQuickKey,
            setQuickKey: setCoreQuickKey,
            quickItems: coreQuickRanges(),
          })}
          {renderChannelInputs({
            channels: coreChannels,
            setChannels: setCoreChannels,
            twoLevel: coreTwoLevel,
            setTwoLevel: setCoreTwoLevel,
            exclusions: coreExclusions,
            setExclusions: setCoreExclusions,
            includeThird: false,
            includeCountry: false,
            includeDevice: false,
          })}
          {renderQueryActions('core')}
        </div>
        <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: 12, padding: 16 }}>
          {visibleCore.length === 0 ? (loading.core ? <TableLoadingState /> : <div style={{ padding: 24, color: 'var(--text3)' }}>暂无数据</div>)
            : visibleCore.map(m => {
              const label = channelPortalMetricLabel(m.label);
              return <div key={m.key} className="portal-core-metric-card" style={{ flex: '1 1 180px', minWidth: 170, border: '1px solid var(--border2)', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
                  <MetricDescriptionTooltip label={label} />
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatSiteMetric(m.key, m.value, m.is_pct ? fmtPct : (CORE_INTEGER_METRIC_KEYS.has(m.key) ? fmtInt : fmtNum), userLevel)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, display: 'flex', gap: 10 }}>
                  <span>日环比 <b style={{ color: m.dod >= 0 ? '#16a34a' : '#dc2626' }}>{isSiteSensitiveMetric(m.key) ? maskSiteMetric(fmtPct(m.dod), 'cost_rate', userLevel) : fmtPct(m.dod)}</b></span>
                  <span>周同比 <b style={{ color: m.wow >= 0 ? '#16a34a' : '#dc2626' }}>{isSiteSensitiveMetric(m.key) ? maskSiteMetric(fmtPct(m.wow), 'cost_rate', userLevel) : fmtPct(m.wow)}</b></span>
                </div>
              </div>;
            })}
          {loading.core && core.length > 0 && <TableLoadingOverlay />}
        </div>
      </Card>

      <Card
        title="核心指标趋势"
        extra={<ReportExportButton reportKey="site_channel_portal_trend" title="渠道门户-核心指标趋势" fileName={`渠道门户_核心指标趋势_${exportSnapshots.trend?.params.start_date ?? ''}_${exportSnapshots.trend?.params.end_date ?? ''}.csv`} params={exportSnapshots.trend?.params ?? {}} columns={trendExportColumns} disabled={!exportSnapshots.trend || loading.trend} />}
      >
        <div className="data-filter-card" style={{ margin: 16, marginBottom: 0 }}>
          {renderDateRow({
            start: trendStartDate,
            end: trendEndDate,
            setStart: setTrendStartDate,
            setEnd: setTrendEndDate,
            quickKey: trendQuickKey,
            setQuickKey: setTrendQuickKey,
            quickItems: coreQuickRanges(),
          })}
          {renderChannelInputs({
            channels: trendChannels,
            setChannels: setTrendChannels,
            twoLevel: trendTwoLevel,
            setTwoLevel: setTrendTwoLevel,
            exclusions: trendExclusions,
            setExclusions: setTrendExclusions,
            includeThird: false,
            includeCountry: false,
            includeDevice: false,
          })}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginTop: 12 }}>
            <div className="dimension-select-row" style={{ display: 'flex' }}>
              <span className="dimension-select-label">数值类</span>
              <MultiSelect
                options={visibleTrendValueMetrics.map(metric => ({ value: metric.key, label: metric.label }))}
                value={visibleTrendValueKeys}
                onChange={values => setTrendValueMetricKeys(values as PortalTrendMetricKey[])}
                placeholder="请选择"
                countLabelFormatter={count => `已选 ${count} 项`}
                searchable
                tagDisplay
              />
            </div>
            <div className="dimension-select-row" style={{ display: 'flex' }}>
              <span className="dimension-select-label">比率类</span>
              <MultiSelect
                options={visibleTrendRatioMetrics.map(metric => ({ value: metric.key, label: metric.label }))}
                value={visibleTrendRatioKeys}
                onChange={values => setTrendRatioMetricKeys(values as PortalTrendMetricKey[])}
                placeholder="请选择"
                countLabelFormatter={count => `已选 ${count} 项`}
                searchable
                tagDisplay
              />
            </div>
          </div>
          {renderQueryActions('trend')}
        </div>
        <div style={{ padding: '8px 16px 16px' }}>
          <TrendChart rows={trend} loading={loading.trend} valueMetrics={visibleTrendValueKeys} ratioMetrics={visibleTrendRatioKeys} />
        </div>
      </Card>

      {/* 会话 */}
      <div ref={sessionSection.ref}>
      <Card
        title="会话"
        extra={<ReportExportButton reportKey="site_channel_portal_session" title="渠道门户-会话" fileName={`渠道门户_会话_${exportSnapshots.session?.params.start_date ?? ''}_${exportSnapshots.session?.params.end_date ?? ''}.csv`} params={exportSnapshots.session?.params ?? {}} columns={portalSessionExportColumns(sessionDimensions)} disabled={!exportSnapshots.session || loading.session} />}
      >
        <div className="data-filter-card" style={{ margin: 16, marginBottom: 0 }}>
          {renderDateRow({
            period: sessionPeriod,
            setPeriod: setSessionPeriod,
            start: sessionStartDate,
            end: sessionEndDate,
            setStart: setSessionStartDate,
            setEnd: setSessionEndDate,
            quickKey: sessionQuickKey,
            setQuickKey: setSessionQuickKey,
          })}
          {renderChannelInputs({
            channels: sessionChannels,
            setChannels: setSessionChannels,
            twoLevel: sessionTwoLevel,
            setTwoLevel: setSessionTwoLevel,
            threeLevel: sessionThreeLevel,
            setThreeLevel: setSessionThreeLevel,
            country: sessionCountry,
            setCountry: setSessionCountry,
            device: sessionDevice,
            setDevice: setSessionDevice,
            source: sessionSource,
            setSource: setSessionSource,
            medium: sessionMedium,
            setMedium: setSessionMedium,
            campaign: sessionCampaign,
            setCampaign: setSessionCampaign,
            exclusions: sessionExclusions,
            setExclusions: setSessionExclusions,
            includeUtm: true,
          })}
          <div className="dimension-select-row" style={{ display: 'flex', marginTop: 12 }}>
            <span className="dimension-select-label">动态维度</span>
            <MultiSelect
              options={portalSessionDimensionOptions}
              value={sessionDimensions}
              onChange={values => setSessionDimensions(fixedPortalDimensionOrder(values, PORTAL_SESSION_DIMENSIONS, DEFAULT_SESSION_DIMENSIONS))}
              placeholder="请选择"
              countLabelFormatter={count => `已选 ${count} 项`}
              searchable
              tagDisplay
            />
          </div>
          {renderQueryActions('session')}
        </div>
        <DataTable<PortalSessionRow>
          dimCols={sessionDimensions.map(key => ({ key, label: PORTAL_SESSION_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key }))}
          rows={session} loading={loading.session}
          summary={sessionSummary}
          cols={[
            { key: 'session_num', label: '会话数(神策)', fmt: fmtInt },
            { key: 'regist_user_num', label: '注册用户数', fmt: fmtInt },
            { key: 'pdp_view_num', label: '商品详情页浏览次数', fmt: fmtInt },
            { key: 'add_cart_num', label: '加购成功次数', fmt: fmtInt },
            { key: 'order_num', label: '支付订单数', fmt: fmtInt },
            { key: 'net_sales', label: '净销售额', fmt: fmtNum },
            { key: 'avg_price', label: '单均价', fmt: fmtNum },
            { key: 'session_reg_rate', label: '会话注册率', fmt: fmtPct },
            { key: 'cvr', label: 'CVR', fmt: fmtPct },
            { key: 'session_cart_rate', label: '会话加购率', fmt: fmtPct },
            { key: 'cart_conv_rate', label: '加购转化率', fmt: fmtPct },
          ]}
        />
      </Card>
      </div>

      {isVooglamPortal && (
        <div ref={vooglamSessionSection.ref}>
          <Card
            title="会话-多维"
            extra={<ReportExportButton reportKey="site_channel_portal_vooglam_session" title="Vooglam渠道门户-会话多维" fileName={`Vooglam渠道门户_会话多维_${exportSnapshots.vooglamSession?.params.start_date ?? ''}_${exportSnapshots.vooglamSession?.params.end_date ?? ''}.csv`} params={exportSnapshots.vooglamSession?.params ?? {}} columns={vooglamSessionExportColumns(vooglamSessionDimensions)} disabled={!exportSnapshots.vooglamSession || loading.vooglamSession} />}
          >
            <div className="data-filter-card" style={{ margin: 16, marginBottom: 0 }}>
              <p style={{ margin: '0 0 12px', color: 'var(--text)', fontWeight: 700, fontStyle: 'italic' }}>fb已剔除：Vooglam-主页账户-0827的花费</p>
              {renderDateRow({
                period: vooglamSessionPeriod,
                setPeriod: setVooglamSessionPeriod,
                start: vooglamSessionStartDate,
                end: vooglamSessionEndDate,
                setStart: setVooglamSessionStartDate,
                setEnd: setVooglamSessionEndDate,
                quickKey: vooglamSessionQuickKey,
                setQuickKey: setVooglamSessionQuickKey,
              })}
              <div className="data-filter-row compact">
                <PortalSelectFilter label="二级渠道" field="two_level_channel" value={vooglamSessionTwoLevel} onChange={setVooglamSessionTwoLevel} size="lg" />
                <PortalSelectFilter label="三级渠道" field="three_level_channel" value={vooglamSessionThreeLevel} onChange={setVooglamSessionThreeLevel} size="lg" />
              </div>
              <div className="dimension-select-row" style={{ display: 'flex', marginTop: 12 }}>
                <span className="dimension-select-label">动态维度</span>
                <MultiSelect
                  options={vooglamSessionDimensionOptions}
                  value={vooglamSessionDimensions}
                  onChange={values => setVooglamSessionDimensions(fixedPortalDimensionOrder(values, VOOGLAM_SESSION_DIMENSIONS, DEFAULT_VOOGLAM_SESSION_DIMENSIONS))}
                  placeholder="请选择"
                  countLabelFormatter={count => `已选 ${count} 项`}
                  searchable
                  tagDisplay
                />
              </div>
              {renderQueryActions('vooglamSession')}
            </div>
            <DataTable<VooglamPortalSessionMultidimRow>
              dimCols={vooglamSessionDimensions.map(key => ({ key, label: VOOGLAM_SESSION_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key }))}
              rows={vooglamSession}
              loading={loading.vooglamSession}
              summary={vooglamSessionSummary}
              cols={[
                { key: 'spend', label: '账户花费', fmt: fmtNum },
                { key: 'clicks', label: '点击次数', fmt: fmtInt },
                { key: 'session_num', label: '会话数（神策）', fmt: fmtInt },
                { key: 'arrival_rate', label: '到达率', fmt: fmtPct },
                { key: 'session_cost', label: '会话成本', fmt: fmtNum },
                { key: 'regist_user_num', label: '注册用户数', fmt: fmtInt },
                { key: 'pdp_view_num', label: '商详页浏览PV', fmt: fmtInt },
                { key: 'add_cart_num', label: '加购成功PV', fmt: fmtInt },
                { key: 'order_num', label: '支付订单数', fmt: fmtInt },
                { key: 'net_sales', label: '净销售额', fmt: fmtNum },
                { key: 'roi', label: '渠道ROI', fmt: fmtNum },
                { key: 'cpa', label: '渠道CPA', fmt: fmtNum },
                { key: 'avg_price', label: '单均价', fmt: fmtNum },
                { key: 'session_reg_rate', label: '会话注册率', fmt: fmtPct },
                { key: 'cvr', label: 'CVR', fmt: fmtPct },
                { key: 'session_cart_rate', label: '会话加购率', fmt: fmtPct },
                { key: 'cart_conv_rate', label: '加购转化率', fmt: fmtPct },
              ]}
            />
          </Card>
          <Card
            title="会话同期对比（FB已剔除）"
            extra={<ReportExportButton reportKey="site_channel_portal_vooglam_session_comparison" title="Vooglam渠道门户-会话同期对比" fileName={`Vooglam渠道门户_会话同期对比_${exportSnapshots.vooglamComparison?.params.start_date ?? ''}_${exportSnapshots.vooglamComparison?.params.end_date ?? ''}.csv`} params={exportSnapshots.vooglamComparison?.params ?? {}} columns={vooglamSessionComparisonExportColumns} disabled={!exportSnapshots.vooglamComparison || loading.vooglamComparison} />}
          >
            <div style={{ padding: '12px 16px 16px' }}>
              <VooglamSessionComparisonTable rows={vooglamSessionComparison} loading={loading.vooglamComparison} />
            </div>
          </Card>
        </div>
      )}

      {/* 用户 */}
      <div ref={userSection.ref}>
      <Card
        title="用户"
        extra={<ReportExportButton reportKey="site_channel_portal_user" title="渠道门户-用户" fileName={`渠道门户_用户_${exportSnapshots.user?.params.start_date ?? ''}_${exportSnapshots.user?.params.end_date ?? ''}.csv`} params={exportSnapshots.user?.params ?? {}} columns={userExportColumns} disabled={!exportSnapshots.user || loading.user} />}
      >
        <div className="data-filter-card" style={{ margin: 16, marginBottom: 0 }}>
          {renderChannelInputs({
            channels: userChannels,
            setChannels: setUserChannels,
            twoLevel: userTwoLevel,
            setTwoLevel: setUserTwoLevel,
            threeLevel: userThreeLevel,
            setThreeLevel: setUserThreeLevel,
            country: userCountry,
            setCountry: setUserCountry,
            device: userDevice,
            setDevice: setUserDevice,
            exclusions: userExclusions,
            setExclusions: setUserExclusions,
          })}
          {renderDateRow({
            period: userPeriod,
            setPeriod: setUserPeriod,
            start: userStartDate,
            end: userEndDate,
            setStart: setUserStartDate,
            setEnd: setUserEndDate,
          })}
          <div className="dimension-select-row" style={{ display: 'flex', marginTop: 12 }}>
            <span className="dimension-select-label">动态维度</span>
            <MultiSelect
              options={portalUserDimensionOptions}
              value={userDimensions}
              onChange={values => setUserDimensions(fixedPortalDimensionOrder(values, PORTAL_USER_DIMENSIONS, DEFAULT_USER_DIMENSIONS))}
              placeholder="请选择"
              countLabelFormatter={count => `已选 ${count} 项`}
              searchable
              tagDisplay
            />
          </div>
          {renderQueryActions('user')}
        </div>
        <DataTable<PortalUserRow>
          dimCols={userDimensionColumns}
          rows={user} loading={loading.user}
          pageSize={20}
          cols={[
            { key: 'visit_user_num', label: '访问UV', fmt: fmtInt },
            { key: 'no_reg_visit_num', label: '未注册访问UV', fmt: fmtInt },
            { key: 'no_reg_visit_rate', label: '未注册访问用户占比', fmt: fmtPct },
            { key: 'first_visit_num', label: '首次访问UV', fmt: fmtInt },
            { key: 'first_visit_rate', label: '首次访问UV占比', fmt: fmtPct },
            { key: 'reg_num', label: '注册用户数', fmt: fmtInt },
            { key: 'reg_rate', label: '当日注册率', fmt: fmtPct },
          ]}
        />
      </Card>
      </div>

      {/* 支付用户数 (周/月) */}
      <div ref={payUserSection.ref}>
      <Card
        title={`支付用户数（${payUserPeriod === 'month' ? '月度' : '周度'}更新，${payUserStartDate} 至 ${payUserEndDate}）`}
        extra={<ReportExportButton reportKey="site_channel_portal_pay_user" title="渠道门户-支付用户数" fileName={`渠道门户_支付用户数_${exportSnapshots.payUser?.params.start_date ?? ''}_${exportSnapshots.payUser?.params.end_date ?? ''}.csv`} params={exportSnapshots.payUser?.params ?? {}} columns={portalPayUserExportColumns(payUserDimensions, payUserPeriod)} disabled={!exportSnapshots.payUser || loading.payUser} />}
      >
        <div className="data-filter-card" style={{ margin: 16, marginBottom: 0 }}>
          {renderChannelInputs({
            channels: payUserChannels,
            setChannels: setPayUserChannels,
            twoLevel: payUserTwoLevel,
            setTwoLevel: setPayUserTwoLevel,
            threeLevel: payUserThreeLevel,
            setThreeLevel: setPayUserThreeLevel,
            country: payUserCountry,
            setCountry: setPayUserCountry,
            device: payUserDevice,
            setDevice: setPayUserDevice,
            exclusions: payUserExclusions,
            setExclusions: setPayUserExclusions,
          })}
          {renderDateRow({
            period: payUserPeriod,
            setPeriod: p => setPayUserPeriod(p === 'month' ? 'month' : 'week'),
            onPeriodChange: p => {
              const next = p === 'month' ? 'month' : 'week';
              setPayUserPeriod(next);
              const r = next === 'month' ? rangeFor('month') : lastCompletedWeekRange();
              setPayUserStartDate(r.start);
              setPayUserEndDate(r.end);
            },
            start: payUserStartDate,
            end: payUserEndDate,
            setStart: setPayUserStartDate,
            setEnd: setPayUserEndDate,
            periods: [{ key: 'week', label: '周' }, { key: 'month', label: '月' }],
          })}
          <div className="dimension-select-row" style={{ display: 'flex', marginTop: 12 }}>
            <span className="dimension-select-label">动态维度</span>
            <MultiSelect
              options={portalPayUserDimensionOptions(payUserPeriod)}
              value={payUserDimensions}
              onChange={values => setPayUserDimensions(fixedPortalDimensionOrder(values, PORTAL_PAY_USER_DIMENSIONS, DEFAULT_PAY_USER_DIMENSIONS))}
              placeholder="请选择"
              countLabelFormatter={count => `已选 ${count} 项`}
              searchable
              tagDisplay
            />
          </div>
          {renderQueryActions('payUser')}
        </div>
        <DataTable<PortalPayUserRow>
          dimCols={payUserDimensions.map(key => ({ key, label: key === 'period' ? (payUserPeriod === 'month' ? '月' : '周') : PORTAL_PAY_USER_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key }))}
          rows={payUser} loading={loading.payUser}
          cols={[
            { key: 'first_order_reg_cur', label: payUserPeriod === 'month' ? '首单当月注册' : '首单当周注册', fmt: fmtInt },
            { key: 'first_order_reg_year', label: payUserPeriod === 'month' ? '首单当年非当月注册' : '首单当年非当周注册', fmt: fmtInt },
            { key: 'first_order_no_year', label: '首单非当年注册', fmt: fmtInt },
            { key: 'new_repeat_num', label: '新客复购', fmt: fmtInt },
            { key: 'act_old_num', label: '活跃老客', fmt: fmtInt },
            { key: 'sleep_old_num', label: '休眠老客', fmt: fmtInt },
          ]}
        />
      </Card>
      </div>

      {/* 商品 */}
      <div ref={productSection.ref}>
      <Card
        title={`商品（${fullNames.length === 0 ? '全部品类' : fullNames.length === 1 ? fullNames[0] : `已选 ${fullNames.length} 个品类`}，按销量 Top 500）`}
        extra={<ReportExportButton reportKey="site_channel_portal_product" title="渠道门户-商品" fileName={`渠道门户_商品_${exportSnapshots.product?.params.start_date ?? ''}_${exportSnapshots.product?.params.end_date ?? ''}.csv`} params={exportSnapshots.product?.params ?? {}} columns={portalProductExportColumns(productDimensions)} disabled={!exportSnapshots.product || loading.product} />}
      >
        <div className="data-filter-card" style={{ margin: 16, marginBottom: 0 }}>
          {renderDateRow({
            period: productPeriod,
            setPeriod: setProductPeriod,
            start: productStartDate,
            end: productEndDate,
            setStart: setProductStartDate,
            setEnd: setProductEndDate,
            quickKey: productQuickKey,
            setQuickKey: setProductQuickKey,
          })}
          {renderChannelInputs({
            channels: productChannels,
            setChannels: setProductChannels,
            twoLevel: productTwoLevel,
            setTwoLevel: setProductTwoLevel,
            threeLevel: productThreeLevel,
            setThreeLevel: setProductThreeLevel,
            exclusions: productExclusions,
            setExclusions: setProductExclusions,
            includeCountry: false,
            includeDevice: false,
          })}
          <div className="data-filter-row compact">
            <PortalProductCategoryFilter site={site} value={fullNames} onChange={setFullNames} exclude={Boolean(productExclusions.full_name)} onExcludeChange={exclude => setProductExclusions(prev => ({ ...prev, full_name: exclude }))} />
            <TextFilter label="商品SKU" value={productSku} onChange={setProductSku} placeholder="不限" size="lg" />
            <TextFilter label="平台SKU" value={businessSku} onChange={setBusinessSku} placeholder="不限" size="lg" />
            <TextFilter label="销售SKU" value={shopSku} onChange={setShopSku} placeholder="不限" size="lg" />
          </div>
          <div className="dimension-select-row" style={{ display: 'flex', marginTop: 12 }}>
            <span className="dimension-select-label">动态维度</span>
            <MultiSelect
              options={portalProductDimensionOptions}
              value={productDimensions}
              onChange={values => setProductDimensions(fixedPortalDimensionOrder(values, PORTAL_PRODUCT_DIMENSIONS, DEFAULT_PRODUCT_DIMENSIONS))}
              placeholder="请选择"
              countLabelFormatter={count => `已选 ${count} 项`}
              searchable
              tagDisplay
            />
          </div>
          {renderQueryActions('product')}
        </div>
        <DataTable<PortalProductRow>
          dimCols={productDimensions.map(key => ({ key, label: PORTAL_PRODUCT_DIMENSIONS.find(dimension => dimension.key === key)?.label ?? key }))}
          rows={product} loading={loading.product}
          cols={[
            { key: 'pdp_view_num', label: '商品详情页浏览次数', fmt: fmtInt },
            { key: 'add_cart_num', label: '加购成功次数', fmt: fmtInt },
            { key: 'quantity', label: '商品销量', fmt: fmtInt },
            { key: 'sales_amt', label: '商品销售额', fmt: fmtNum },
            { key: 'avg_price', label: '商品均价', fmt: fmtNum },
            { key: 'view_conv_rate', label: '浏览销量转化率', fmt: fmtPct },
          ]}
        />
      </Card>
      </div>

    </div>
  );
}
